import useBlockedUsers from '@contexts/useBlockedUsers';
import { useGetMyConnectionsWithProfiles } from '@sunrise-ui/api-client';
import type { Profile } from '@sunrise-ui/api/profile';
import {
  Colors,
  Loader,
  TextInput,
  TextReg,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
  type DimensionValue,
} from 'react-native';

import { Search01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import ProfileResult from '@molecules/ProfileResult';
import { TextInput as PaperTextInput } from 'react-native-paper';

type Props = {
  onSelect: (profile: Profile) => void | Promise<void>;
  width?: DimensionValue;
  maxHeight?: number;
  hiddenIds?: string[];
  placeholderLabel?: string;
  minChars?: number;
  profileResultSize?: 'small' | 'medium';
};

export default function ConnectionSearch({
  onSelect,
  maxHeight,
  hiddenIds,
  placeholderLabel = 'Search connections...',
  minChars = 1,
  profileResultSize = 'small',
}: Props) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showResults, setShowResults] = useState(true);
  const shouldShowResults = Boolean(showResults);
  const styles = useDynamicStyles(createStyles, {
    shouldShowResults,
    maxHeight,
  });

  const { isUserBlocked } = useBlockedUsers();
  const { data: connections, isLoading } = useGetMyConnectionsWithProfiles();

  // Debounce logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Show results when user starts typing or focuses
  useEffect(() => {
    if (search) {
      setShowResults(true);
    }
  }, [search]);

  // Filter connections based on search
  const filteredConnections = useMemo(() => {
    if (!connections) return [];

    let filtered = connections.filter(
      (profile) => !isUserBlocked(profile.user_id),
    );

    if (hiddenIds?.length) {
      filtered = filtered.filter(
        (profile) => !hiddenIds.includes(profile.user_id),
      );
    }

    if (debouncedSearch.length >= minChars) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter((profile) => {
        const fullName =
          `${profile.first_name} ${profile.last_name}`.toLowerCase();
        return fullName.includes(searchLower);
      });
    }

    return filtered;
  }, [connections, debouncedSearch, hiddenIds, isUserBlocked, minChars]);

  const handleSelect = async (profile: Profile) => {
    await onSelect(profile);
    setShowResults(false);
    setSearch('');
  };

  return (
    <View style={styles.container}>
      <TextInput
        label={placeholderLabel}
        showLabel={false}
        value={search}
        onChange={setSearch}
        onFocus={() => setShowResults(true)}
        style={styles.searchInput}
        autoCorrect={false}
        left={
          <PaperTextInput.Icon
            icon={() => (
              <HugeiconsIcon icon={Search01Icon} color={Colors.text} />
            )}
          />
        }
      />
      {shouldShowResults && (
        <FlatList
          data={filteredConnections}
          bounces={false}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          renderItem={({ item: profile }) => (
            <TouchableOpacity
              key={profile.user_id}
              style={styles.listItem}
              onPress={() => handleSelect(profile)}
            >
              <ProfileResult
                userId={profile.user_id}
                size={profileResultSize}
              />
            </TouchableOpacity>
          )}
          keyExtractor={(item) => item.user_id}
          windowSize={4}
          initialNumToRender={10}
          contentContainerStyle={styles.listContent}
          style={styles.list}
          ListEmptyComponent={
            isLoading ? (
              <View style={styles.emptyListItem}>
                <Loader />
              </View>
            ) : (
              <View style={styles.emptyListItem}>
                <TextReg>
                  {connections?.length === 0
                    ? 'No connections yet'
                    : 'No matching connections'}
                </TextReg>
              </View>
            )
          }
        />
      )}
    </View>
  );
}

const createStyles = ({
  isMobile,
  maxHeight,
  shouldShowResults,
}: WithResponsive<Props & { shouldShowResults: boolean }>) => {
  const borderRadius = 24;
  return StyleSheet.create({
    container: {
      flexGrow: 1,
      flexShrink: 1,
      maxHeight,
    },
    searchInput: {
      backgroundColor: Colors.opacity05,
      borderTopRightRadius: borderRadius,
      borderTopLeftRadius: borderRadius,
      borderBottomRightRadius: shouldShowResults ? 0 : borderRadius,
      borderBottomLeftRadius: shouldShowResults ? 0 : borderRadius,
    },
    list: {
      borderBottomRightRadius: borderRadius,
      borderBottomLeftRadius: borderRadius,
    },
    listContent: {
      backgroundColor: Colors.opacity05,
      paddingHorizontal: isMobile ? 12 : 24,
      borderBottomRightRadius: borderRadius,
      borderBottomLeftRadius: borderRadius,
    },
    listItem: {
      display: 'flex',
      flexDirection: 'row',
      gap: 20,
      alignItems: 'center',
      marginBottom: 20,
    },
    emptyListItem: {
      paddingVertical: isMobile ? 12 : 16,
    },
  });
};
